import { useOrganizationName } from './useOrganizationName'
import {
  UseStaleBannerStateResultIProps,
  useStaleBannerState,
} from './useStaleBannerState'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { useTeamStaleIncidentsCountQuery } from '@/graphql/generated/hooks'

import { TEAM_STALE_WORK_ANALYTICS } from '../constants/constants'

interface UseTeamStaleWorkResultIProps extends UseStaleBannerStateResultIProps {
  /** Undefined until the org query resolves; visibility already waits on it. */
  orgName?: string
}

/**
 * The team stale work banner (all users, ENG-32130): how many unresolved
 * incidents anyone acknowledged over an hour ago, org-wide. Gated on the
 * feature flag only — the former PM role gate is gone; operators see the
 * same program-wide view. Lifecycle and analytics live in
 * useStaleBannerState. Owns the org-name fetch (ENG-32179) so visibility —
 * and with it the view event — waits for the interpolated copy to be
 * renderable, and so the banner can hand the resolved name to the modal.
 */
export const useTeamStaleWork = (): UseTeamStaleWorkResultIProps => {
  const isStaleWorkBannerEnabled = useFeatureFlag(FeatureFlag.staleWorkBanner)
  // An off flag must cost zero network requests, org query included.
  const { orgName } = useOrganizationName({ skip: !isStaleWorkBannerEnabled })

  const staleBannerState = useStaleBannerState({
    analytics: TEAM_STALE_WORK_ANALYTICS,
    buildVariables: (cutoff) => ({ actionUpdatedAtLte: cutoff }),
    isEnabled: isStaleWorkBannerEnabled,
    isReady: Boolean(orgName),
    sentryAction: 'fetch_team_count',
    useCountQuery: useTeamStaleIncidentsCountQuery,
  })

  return { ...staleBannerState, orgName }
}
